import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Briefcase, MapPin, Phone, Navigation, Clock, CheckCircle2,
  AlertCircle, User, Calendar, ChevronRight, Wrench
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CrewPortal = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('today');

  useEffect(() => {
    fetchCrewJobs();
  }, []);

  const fetchCrewJobs = async () => {
    try {
      // Fetch jobs assigned to current user/crew
      const response = await axios.get(`${API}/jobs`);
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching crew jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      scheduled: { label: 'Scheduled', className: 'bg-blue-100 text-blue-800 dark:bg-blue-950' },
      ready_for_scheduling: { label: 'Ready', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950' },
      confirmed: { label: 'Confirmed', className: 'bg-purple-100 text-purple-800 dark:bg-purple-950' },
      in_progress: { label: 'On Site', className: 'bg-green-100 text-green-800 dark:bg-green-950' },
      completed: { label: 'Completed', className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950' },
      on_hold: { label: 'Needs Attention', className: 'bg-red-100 text-red-800 dark:bg-red-950' }
    };

    const statusConfig = config[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return <Badge className={statusConfig.className}>{statusConfig.label}</Badge>;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return 'Not set';
    try {
      return new Date(dateStr).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '';
    }
  };

  const openInMaps = (address, city, state, zip) => {
    const fullAddress = `${address}, ${city}, ${state} ${zip}`;
    const encodedAddress = encodeURIComponent(fullAddress);
    // Open in Google Maps (works on mobile and desktop)
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
  };

  const callCustomer = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  const filterJobsByTab = (jobsList) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (activeTab) {
      case 'today':
        return jobsList.filter(job => {
          if (!job.scheduled_start) return false;
          const jobDate = new Date(job.scheduled_start);
          jobDate.setHours(0, 0, 0, 0);
          return jobDate.getTime() === today.getTime() || job.status === 'in_progress';
        });
      case 'upcoming':
        return jobsList.filter(job => {
          if (!job.scheduled_start) return false;
          const jobDate = new Date(job.scheduled_start);
          jobDate.setHours(0, 0, 0, 0);
          return jobDate.getTime() > today.getTime() && job.status !== 'completed';
        });
      case 'completed':
        return jobsList.filter(job => job.status === 'completed');
      default:
        return jobsList;
    }
  };

  const filteredJobs = filterJobsByTab(jobs);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading your jobs...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-4 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Wrench className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Crew Portal</h1>
              <p className="text-sm text-muted-foreground">Your jobs for the field</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full">
            <TabsTrigger value="today" className="flex-1">
              <Clock className="h-4 w-4 mr-2" />
              Today
            </TabsTrigger>
            <TabsTrigger value="upcoming" className="flex-1">
              <Calendar className="h-4 w-4 mr-2" />
              Upcoming
            </TabsTrigger>
            <TabsTrigger value="completed" className="flex-1">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Completed
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Jobs List */}
        <div className="space-y-3">
          {filteredJobs.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Briefcase className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-2">
                  {activeTab === 'today' && 'No jobs scheduled for today'}
                  {activeTab === 'upcoming' && 'No upcoming jobs'}
                  {activeTab === 'completed' && 'No completed jobs yet'}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredJobs.map(job => (
              <Card 
                key={job.id} 
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/crew/jobs/${job.id}`)}
              >
                <CardContent className="p-4">
                  {/* Job Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg">
                          {job.customer_name}
                        </h3>
                        {getStatusBadge(job.status)}
                      </div>
                      <p className="text-sm text-muted-foreground">{job.job_type}</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </div>

                  {/* Time */}
                  {job.scheduled_start && (
                    <div className="flex items-center gap-2 text-sm mb-3">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span>{formatDate(job.scheduled_start)} at {formatTime(job.scheduled_start)}</span>
                    </div>
                  )}

                  {/* Location */}
                  <div className="flex items-start gap-2 text-sm mb-3">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div className="flex-1">
                      <div>{job.address}</div>
                      <div className="text-muted-foreground">{job.city}, {job.state} {job.zip_code}</div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex gap-2 pt-3 border-t">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        openInMaps(job.address, job.city, job.state, job.zip_code);
                      }}
                      className="flex-1"
                    >
                      <Navigation className="h-4 w-4 mr-2" />
                      Navigate
                    </Button>
                    {job.customer_phone && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          callCustomer(job.customer_phone);
                        }}
                        className="flex-1"
                      >
                        <Phone className="h-4 w-4 mr-2" />
                        Call
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CrewPortal;
